import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { CalendarDays, Search, MapPin, Clock, Users, RefreshCw, Loader2, Calendar, ArrowRight, Plus, Edit, Trash2, Save, X, Repeat, BarChart3, Mail, FileText } from "lucide-react";
import { format, isPast, isFuture, isToday, differenceInDays } from "date-fns";
import { es } from "date-fns/locale";
import MultiPersonSelect, { COVERAGE_TEAM, shortName } from "../components/MultiPersonSelect";
import FollowUpDialog from "../components/FollowUpDialog";
import KpiDashboard from "../components/KpiDashboard";
import EventosCalendarTab from "../components/eventos/EventosCalendarTab";

const API = process.env.REACT_APP_BACKEND_URL;

const EVENT_TYPES = [
  { value: "deadline", label: "Fecha limite" },
  { value: "meeting", label: "Reunion" },
  { value: "conference", label: "Conferencia" },
  { value: "workshop", label: "Taller/Workshop" },
  { value: "presentation", label: "Presentacion" },
  { value: "milestone", label: "Hito" },
  { value: "other", label: "Otro" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Baja", color: "text-gray-500" },
  { value: "medium", label: "Media", color: "text-amber-500" },
  { value: "high", label: "Alta", color: "text-red-500" },
];

function EventCard({ evento, onEdit, onDelete, onFollowUp, onRecurring, onReminder, isPastEvent }) {
  const covered = evento.covered_by || [];
  const coveredList = Array.isArray(covered) ? covered : (covered ? [covered] : []);

  const getEventBadge = () => {
    if (!evento.date) return <Badge variant="outline">Sin fecha</Badge>;
    const d = new Date(evento.date);
    const days = differenceInDays(d, new Date());
    if (isToday(d)) return <Badge className="bg-red-500 text-white">Hoy</Badge>;
    if (days > 0 && days <= 7) return <Badge className="bg-amber-500 text-white">En {days} dias</Badge>;
    if (days > 7 && days <= 30) return <Badge className="bg-blue-500 text-white">En {days} dias</Badge>;
    if (isPast(d)) return <Badge variant="secondary">Pasado</Badge>;
    return <Badge variant="outline">{format(d, "MMM yyyy", { locale: es })}</Badge>;
  };

  return (
    <Card className={`hover:shadow-lg transition-shadow ${isPastEvent ? "opacity-75 hover:opacity-100" : ""}`} data-testid={`evento-${evento.id}`}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{evento.name}</CardTitle>
            {evento.description && <CardDescription className="mt-1 line-clamp-2">{evento.description}</CardDescription>}
          </div>
          <div className="flex gap-0.5">
            {isPastEvent && (
              <>
                <Button size="icon" variant="ghost" onClick={() => onFollowUp(evento)} title="Seguimiento"><FileText className="w-4 h-4 text-green-600" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onReminder(evento)} title="Enviar recordatorio"><Mail className="w-4 h-4 text-blue-500" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onRecurring(evento)} title="Recurrente"><Repeat className="w-4 h-4 text-purple-500" /></Button>
              </>
            )}
            <Button size="icon" variant="ghost" onClick={() => onEdit(evento)} data-testid={`evento-edit-${evento.id}`}><Edit className="w-4 h-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete(evento)} data-testid={`evento-delete-${evento.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span>{evento.date ? format(new Date(evento.date), "d MMMM yyyy", { locale: es }) : "Sin fecha"}</span>
          {evento.start_time && <span className="text-muted-foreground" data-testid={`evento-time-${evento.id}`}>{evento.start_time}{evento.end_time ? ` - ${evento.end_time}` : ""}</span>}
          {getEventBadge()}
        </div>
        {evento.location && <div className="flex items-center gap-2 text-sm text-muted-foreground"><MapPin className="w-4 h-4" /><span>{evento.location}</span></div>}
        {coveredList.length > 0 && (
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <Users className="w-4 h-4 text-blue-500" />
            {coveredList.map(name => <Badge key={name} className="bg-blue-100 text-blue-700 text-xs">{name}</Badge>)}
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          {evento.event_type && <Badge variant="outline">{EVENT_TYPES.find(t => t.value === evento.event_type)?.label || evento.event_type}</Badge>}
          {evento.priority && <Badge variant="outline" className={PRIORITY_OPTIONS.find(p => p.value === evento.priority)?.color}>{PRIORITY_OPTIONS.find(p => p.value === evento.priority)?.label}</Badge>}
          {evento.is_recurring && <Badge className="bg-purple-500 text-white hover:bg-purple-600"><Repeat className="w-3 h-3 mr-1" />Recurrente</Badge>}
          {coveredList.length === 0 && <Badge variant="outline" className="text-amber-600 border-amber-300">Sin asignar</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function EventosPage() {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPast, setShowPast] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [coverageStats, setCoverageStats] = useState({});
  const [activeTab, setActiveTab] = useState("eventos");
  const [editDialog, setEditDialog] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [isNewEvento, setIsNewEvento] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [recurringDialog, setRecurringDialog] = useState(false);
  const [recurringEvento, setRecurringEvento] = useState(null);
  const [followUpEvento, setFollowUpEvento] = useState(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const { token, user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/eventos`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setEventos(await res.json());
    } catch {}
    setLoading(false);
  }, [token]);

  const fetchCoverageStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/eventos/coverage-stats`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setCoverageStats(data.stats || {}); }
    } catch {}
  }, [token]);

  useEffect(() => { fetchData(); fetchCoverageStats(); }, [fetchData, fetchCoverageStats]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API}/api/eventos/sync`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const r = await res.json(); toast.success(`Sincronizados ${r.synced} eventos`); fetchData(); }
      else toast.error("Error al sincronizar");
    } catch { toast.error("Error al sincronizar"); }
    setSyncing(false);
  };

  const emptyForm = () => ({ name: "", description: "", event_type: "deadline", date: new Date().toISOString().split("T")[0], end_date: "", start_time: "", end_time: "", location: "", category: "", priority: "medium", is_recurring: false, recurrence_pattern: "yearly", reminder_days: 7, covered_by: [] });

  const openNew = () => { setIsNewEvento(true); setSelectedEvento(null); setEditForm(emptyForm()); setEditDialog(true); };

  const openEdit = (e) => {
    setIsNewEvento(false); setSelectedEvento(e);
    const cb = e.covered_by || [];
    setEditForm({ name: e.name || "", description: e.description || "", event_type: e.event_type || "deadline", date: e.date ? e.date.split("T")[0] : "", end_date: e.end_date ? e.end_date.split("T")[0] : "", start_time: e.start_time || "", end_time: e.end_time || "", location: e.location || "", category: e.category || "", priority: e.priority || "medium", is_recurring: e.is_recurring || false, recurrence_pattern: e.recurrence_pattern || "yearly", reminder_days: e.reminder_days || 7, covered_by: Array.isArray(cb) ? cb : (cb ? [cb] : []) });
    setEditDialog(true);
  };

  const handleSave = async () => {
    if (!editForm.name || !editForm.date) { toast.error("Nombre y fecha son obligatorios"); return; }
    setSaving(true);
    try {
      const url = isNewEvento ? `${API}/api/eventos` : `${API}/api/eventos/${selectedEvento.id}`;
      const res = await fetch(url, { method: isNewEvento ? "POST" : "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(editForm) });
      if (res.ok) { toast.success(isNewEvento ? "Evento creado" : "Evento actualizado"); setEditDialog(false); fetchData(); fetchCoverageStats(); }
      else toast.error("Error al guardar");
    } catch { toast.error("Error al guardar"); }
    setSaving(false);
  };

  const handleDelete = async (e) => {
    if (!confirm(`Eliminar "${e.name}"?`)) return;
    try {
      const res = await fetch(`${API}/api/eventos/${e.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { toast.success("Evento eliminado"); fetchData(); fetchCoverageStats(); }
    } catch { toast.error("Error"); }
  };

  const handleRecurring = async (createNext = false) => {
    if (!recurringEvento) return;
    try {
      const res = await fetch(`${API}/api/eventos/${recurringEvento.id}/make-recurring`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ recurrence_pattern: "yearly", create_next_occurrence: createNext }) });
      if (res.ok) { toast.success(createNext ? "Evento creado para proximo año" : "Marcado recurrente"); setRecurringDialog(false); fetchData(); }
    } catch { toast.error("Error"); }
  };

  const handleReminder = async (e) => {
    try {
      const res = await fetch(`${API}/api/eventos/${e.id}/send-reminder`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); toast.success(d.message); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Error"); }
    } catch { toast.error("Error"); }
  };

  const filtered = eventos
    .filter(e => {
      const match = e.name?.toLowerCase().includes(searchTerm.toLowerCase()) || e.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const past = e.date && isPast(new Date(e.date));
      return match && (showPast || !past) && (typeFilter === "all" || e.event_type === typeFilter);
    })
    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));

  const upcoming = filtered.filter(e => e.date && (isFuture(new Date(e.date)) || isToday(new Date(e.date))));
  const past = filtered.filter(e => e.date && isPast(new Date(e.date)) && !isToday(new Date(e.date)));

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  const cardProps = { onEdit: openEdit, onDelete: handleDelete, onFollowUp: (e) => { setFollowUpEvento(e); setFollowUpOpen(true); }, onRecurring: (e) => { setRecurringEvento(e); setRecurringDialog(true); }, onReminder: handleReminder };

  return (
    <div className="space-y-6 animate-in" data-testid="eventos-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2"><CalendarDays className="w-8 h-8 text-primary" />Eventos Innovación</h1>
          <p className="text-muted-foreground mt-1">{upcoming.length} eventos proximos / {eventos.length} total</p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button variant="outline" onClick={handleSync} disabled={syncing} data-testid="sync-airtable-btn">{syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}Sync Airtable</Button>
          )}
          <Button onClick={openNew} data-testid="add-evento-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Evento</Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="eventos" className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />Eventos</TabsTrigger>
          <TabsTrigger value="calendario" className="flex items-center gap-1" data-testid="tab-calendario"><Calendar className="w-4 h-4" />Calendario</TabsTrigger>
          <TabsTrigger value="kpis" className="flex items-center gap-1"><BarChart3 className="w-4 h-4" />KPIs</TabsTrigger>
        </TabsList>
        <TabsContent value="eventos" className="space-y-6">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5 text-blue-500" />Cobertura por Persona</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                {COVERAGE_TEAM.filter(name => (coverageStats[name]?.count || 0) > 0).map(name => {
                  const s = coverageStats[name] || { count: 0 };
                  return <div key={name} className="text-center p-3 bg-muted rounded-lg min-w-[120px]" data-testid={`coverage-tile-${name}`}><p className="text-2xl font-bold text-blue-600">{s.count}</p><p className="text-xs text-muted-foreground mt-1">{shortName(name)}</p></div>;
                })}
                <div className="text-center p-3 bg-amber-50 rounded-lg min-w-[120px]"><p className="text-2xl font-bold text-amber-600">{coverageStats["Sin asignar"]?.count || 0}</p><p className="text-xs text-muted-foreground mt-1">Sin asignar</p></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col md:flex-row gap-4 items-center">
                <div className="flex-1 relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><Input placeholder="Buscar eventos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div>
                <Select value={typeFilter} onValueChange={setTypeFilter}><SelectTrigger className="w-[180px]"><SelectValue placeholder="Tipo" /></SelectTrigger><SelectContent><SelectItem value="all">Todos</SelectItem>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select>
                <div className="flex items-center gap-2"><Switch id="show-past" checked={showPast} onCheckedChange={setShowPast} /><Label htmlFor="show-past" className="text-sm">Mostrar pasados</Label></div>
              </div>
            </CardContent>
          </Card>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2"><ArrowRight className="w-5 h-5 text-primary" />Proximos ({upcoming.length})</h2>
            {upcoming.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground"><CalendarDays className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No hay eventos proximos</p><Button variant="outline" className="mt-4" onClick={openNew}><Plus className="w-4 h-4 mr-2" />Crear evento</Button></CardContent></Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{upcoming.map(e => <EventCard key={e.id} evento={e} {...cardProps} isPastEvent={false} />)}</div>
            )}
          </div>
          {showPast && past.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-xl font-semibold flex items-center gap-2 text-muted-foreground"><Clock className="w-5 h-5" />Pasados ({past.length})</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{past.map(e => <EventCard key={e.id} evento={e} {...cardProps} isPastEvent={true} />)}</div>
            </div>
          )}
        </TabsContent>
        <TabsContent value="kpis"><KpiDashboard token={token} /></TabsContent>
        <TabsContent value="calendario">
          <EventosCalendarTab eventos={eventos} onEventClick={openEdit} />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{isNewEvento ? "Nuevo Evento" : "Editar Evento"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div className="space-y-2"><Label>Nombre *</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Nombre del evento" data-testid="evento-name-input" /></div>
            <div className="space-y-2"><Label>Descripcion</Label><Textarea value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Descripcion" rows={2} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha *</Label><Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="evento-date-input" /></div>
              <div className="space-y-2"><Label>Fecha Fin</Label><Input type="date" value={editForm.end_date} onChange={(e) => setEditForm({ ...editForm, end_date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Hora inicio</Label><Input type="time" value={editForm.start_time} onChange={(e) => setEditForm({ ...editForm, start_time: e.target.value })} data-testid="evento-start-time-input" /></div>
              <div className="space-y-2"><Label>Hora fin</Label><Input type="time" value={editForm.end_time} onChange={(e) => setEditForm({ ...editForm, end_time: e.target.value })} data-testid="evento-end-time-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo</Label><Select value={editForm.event_type} onValueChange={(v) => setEditForm({ ...editForm, event_type: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-2"><Label>Prioridad</Label><Select value={editForm.priority} onValueChange={(v) => setEditForm({ ...editForm, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITY_OPTIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div className="space-y-2"><Label>Ubicacion</Label><Input value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} placeholder="Lugar" /></div>
            <div className="space-y-2"><Label>Categoria</Label><Input value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} placeholder="Ej: Convocatoria..." /></div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" />Quienes cubren el evento</Label>
              <MultiPersonSelect selected={editForm.covered_by || []} onChange={(v) => setEditForm({ ...editForm, covered_by: v })} />
            </div>
            <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center gap-2"><Repeat className="w-4 h-4 text-purple-500" /><Label>Recurrente</Label></div>
              <Switch checked={editForm.is_recurring} onCheckedChange={(v) => setEditForm({ ...editForm, is_recurring: v })} />
            </div>
            {editForm.is_recurring && (
              <div className="space-y-2"><Label>Recurrencia</Label><Select value={editForm.recurrence_pattern} onValueChange={(v) => setEditForm({ ...editForm, recurrence_pattern: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="yearly">Anual</SelectItem><SelectItem value="monthly">Mensual</SelectItem><SelectItem value="quarterly">Trimestral</SelectItem></SelectContent></Select></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(false)}><X className="w-4 h-4 mr-2" />Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="evento-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}{isNewEvento ? "Crear" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recurring Dialog */}
      <Dialog open={recurringDialog} onOpenChange={setRecurringDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Hacer Evento Recurrente</DialogTitle></DialogHeader>
          <p className="text-muted-foreground">Quieres que &quot;{recurringEvento?.name}&quot; se repita cada año?</p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setRecurringDialog(false)}>Cancelar</Button>
            <Button variant="secondary" onClick={() => handleRecurring(false)}>Solo marcar</Button>
            <Button onClick={() => handleRecurring(true)}><Plus className="w-4 h-4 mr-2" />Crear proximo año</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FollowUpDialog evento={followUpEvento} open={followUpOpen} onClose={setFollowUpOpen} token={token} onSaved={fetchData} />
    </div>
  );
}
